"use client";

import Image from "next/image";
import { useEffect } from "react";
import { AuthButton } from "@/components/auth-button";
import { useAuth } from "@/components/auth-provider";
import { PhoneFrame } from "@/components/phone-frame";
import { PriceText } from "@/components/price-text";
import { useStartingPrice } from "@/hooks/use-starting-price";
import { cdnImages } from "@/lib/cdn-images";
import { piEvents, setExposureSource } from "@/lib/analytics";
import { DEFAULT_ORIGINAL_PRICE, HERO_COPY, TRUST_BADGES } from "@/lib/site-copy";
import {
  PI_CTA_LOCATION,
  PI_CTA_NAME,
  PI_DESTINATION,
  PI_EXPOSURE_SOURCE,
  PI_LOGIN_ENTRY_POINT,
  PI_SECTION,
} from "@/lib/analytics/schema";

export function HeroSection() {
  const startingPrice = useStartingPrice();
  const { isSignedIn } = useAuth();

  useEffect(() => {
    piEvents.pageViewed();
  }, []);

  return (
    <section id="hero" className="relative overflow-hidden bg-pi-bg">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(70%_100%_at_82%_35%,rgba(90,75,218,0.2)_0%,rgba(9,9,11,0)_62%),radial-gradient(110%_130%_at_0%_0%,rgba(90,75,218,0.35)_0%,rgba(9,9,11,0)_55%)]"
      />
      <div className="relative z-10 mx-auto flex max-w-300 flex-wrap items-center gap-6 px-4 pt-10 sm:gap-8 sm:px-6 lg:gap-12 lg:px-8 lg:pt-18">
        <div className="min-w-0 max-w-150 flex-[1_1_440px] pb-10 lg:pb-18">
          <h1 className="m-0 text-balance text-[clamp(34px,5.2vw,56px)] font-bold leading-[1.06] text-pi-bright">
            {HERO_COPY.titleLine1}
            <br />
            <span className="bg-gradient-to-b from-pi-gold-start to-pi-gold bg-clip-text text-transparent">
              {HERO_COPY.titleLine2}
            </span>
          </h1>

          {/* Faded in once pricing loads, rather than height-reserved, to match the mock's spacing. */}
          <div
            className={`mt-[22px] flex flex-wrap items-center gap-3.5 transition-opacity duration-200 sm:gap-5 ${
              startingPrice ? "opacity-100" : "opacity-0"
            }`}
          >
            <span className="relative inline-block text-[clamp(30px,3.6vw,40px)] font-semibold text-pi-dim">
              {startingPrice?.originalPrice ?? DEFAULT_ORIGINAL_PRICE}
              <span
                aria-hidden="true"
                className="absolute top-1/2 -right-[6%] -left-[6%] h-1 -rotate-6 rounded bg-[#FF3B30]"
              />
            </span>
            <div className="flex items-end">
              <span className="bg-gradient-to-b from-pi-gold-start to-pi-gold bg-clip-text text-[clamp(52px,8.6vw,92px)] leading-[0.9] font-bold tracking-tight text-transparent">
                <PriceText value={startingPrice?.amount}/>
              </span>
              <span className="pb-1.5 text-[clamp(15px,1.6vw,18px)] font-semibold text-pi-bright">
                {startingPrice?.suffix ?? "/month"}
              </span>
            </div>
          </div>
          <p className="mt-3 mb-0 text-[15px] text-pi-text">
            {HERO_COPY.tagline}{" "}
            <span className="font-semibold text-pi-accent-soft">
              {HERO_COPY.taglineAccent}
            </span>
          </p>

          <div className="mt-6.5 flex flex-wrap items-center gap-3.5">
            <AuthButton
              size="hero"
              variant="gold"
              className="animate-[pulse-gold_2.4s_ease-in-out_infinite]"
              loginEntryPoint={PI_LOGIN_ENTRY_POINT.HERO_START_FREE}
              trackClick={() =>
                piEvents.ctaClicked({
                  ctaName: PI_CTA_NAME.START_LEARNING_FREE,
                  ctaLocation: PI_CTA_LOCATION.HERO,
                  destination: isSignedIn
                    ? PI_DESTINATION.PW_HOME
                    : PI_DESTINATION.LOGIN_MODAL,
                })
              }
            >
              {HERO_COPY.ctaPrefix}{" "}
              <PriceText value={startingPrice?.amount} className="min-w-[4ch]" />
              {HERO_COPY.ctaSuffix}
            </AuthButton>
          </div>

          <div className="mt-5.5 flex flex-wrap items-center gap-3">
            <span
              aria-hidden="true"
              className="bg-gradient-to-b from-pi-gold-start to-pi-gold bg-clip-text text-lg tracking-[2px] text-transparent"
            >
              ★★★★★
            </span>
            <span className="text-[15px] text-pi-text">
              <strong className="font-bold text-pi-bright">
                {HERO_COPY.ratingScore}
              </strong>{" "}
              {HERO_COPY.ratingSource}&nbsp;&nbsp;·&nbsp;&nbsp;
              <strong className="font-bold text-pi-bright">
                {HERO_COPY.learnerCount}
              </strong>{" "}
              {HERO_COPY.learnerLabel}
            </span>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {TRUST_BADGES.map(
              (badge) => (
                <span
                  key={badge}
                  className="inline-flex items-center gap-1.5 rounded-full border border-pi-border-muted bg-white/[0.04] px-3.5 py-1.5 text-[13px] text-pi-text"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className="shrink-0 text-pi-success"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  {badge}
                </span>
              ),
            )}
          </div>

          <a
            href="#pricing"
            onClick={() => {
              setExposureSource(
                PI_SECTION.PRICING.name,
                PI_EXPOSURE_SOURCE.SEE_PLANS_CTA,
              );
              piEvents.ctaClicked({
                ctaName: PI_CTA_NAME.SEE_PLANS,
                ctaLocation: PI_CTA_LOCATION.HERO,
                destination: PI_DESTINATION.PRICING_SECTION,
              });
            }}
            className="mt-4.5 inline-flex items-center gap-1.5 text-[15px] font-medium text-pi-accent-soft no-underline transition-colors hover:text-pi-bright"
          >
            {HERO_COPY.seePlans}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          </a>
        </div>

        <div className="relative hidden min-w-0 flex-[1_1_440px] items-end justify-center self-stretch md:flex">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-[-10%] left-1/2 h-[110%] w-[120%] -translate-x-1/2 bg-[radial-gradient(55%_60%_at_50%_75%,rgba(90,75,218,0.36)_0%,rgba(9,9,11,0)_70%)]"
          />
          <PhoneFrame
            className="w-[clamp(140px,14vw,180px)] -translate-y-11.5 -rotate-6 rounded-[30px] px-2 pt-3.25 pb-2.5"
            notchClassName="top-[7px] w-[34px]"
          >
            <Image
              src={cdnImages.conceptLecture}
              alt="Concept lectures on Pi"
              width={164}
              height={328}
              sizes="180px"
              className="block aspect-1/2 h-auto w-full rounded-[22px] object-cover"
            />
          </PhoneFrame>
          {/* No `priority`: this whole column is `display:none` below `md`, so preloading it
              only burns mobile bandwidth ahead of the stylesheet and font. */}
          <Image
            src={cdnImages.heroAlakh}
            alt="Alakh Pandey — Physics Wallah"
            width={460}
            height={520}
            sizes="(max-width: 1024px) 340px, 460px"
            className="relative z-2 mx-[clamp(-84px,-6vw,-60px)] block h-auto w-[clamp(340px,33vw,460px)] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
          />
          <PhoneFrame
            className="w-[clamp(140px,14vw,180px)] translate-y-13.5 rotate-6 rounded-[30px] px-2 pt-3.25 pb-2.5"
            notchClassName="top-[7px] w-[34px]"
          >
            <Image
              src={cdnImages.askAi}
              alt="Ask AI on Pi"
              width={164}
              height={328}
              sizes="180px"
              className="block aspect-1/2 h-auto w-full rounded-[22px] object-cover"
            />
          </PhoneFrame>
        </div>
      </div>
    </section>
  );
}
